import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { GALLERY_ITEMS, ALL_TAG } from './galleryData';
import GalleryCard from '../components/GalleryCard';
import Lightbox from '../components/Lightbox';

export default function Gallery() {
  const [activeTag, setActiveTag] = useState(ALL_TAG);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const tags = useMemo(() => {
    const s = new Set([ALL_TAG]);
    GALLERY_ITEMS.forEach((i) => i.tags.forEach((t) => s.add(t)));
    return Array.from(s);
  }, []);

  const filtered = useMemo(() => {
    if (activeTag === ALL_TAG) return GALLERY_ITEMS;
    return GALLERY_ITEMS.filter((i) => i.tags.includes(activeTag));
  }, [activeTag]);

  const openAt = (idx) => setLightboxIndex(idx);
  const close = () => setLightboxIndex(null);
  const prev = () => setLightboxIndex((i) => (i === null ? null : (i + filtered.length - 1) % filtered.length));
  const next = () => setLightboxIndex((i) => (i === null ? null : (i + 1) % filtered.length));

  // Deep link: open by id param on mount / when tag changes
  useEffect(() => {
    const id = searchParams.get('id');
    if (!id) return;
    const idx = filtered.findIndex((i) => i.id === id);
    if (idx !== -1) setLightboxIndex(idx);
  }, [searchParams, filtered]);

  // Update deep link when opening/closing
  useEffect(() => {
    if (lightboxIndex !== null) {
      const id = filtered[lightboxIndex]?.id;
      if (id) setSearchParams({ id });
    } else {
      if (searchParams.get('id')) setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex]);

  return (
    <main className="bg-dark text-white min-h-screen pt-24 pb-20">
      {/* Page Header */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl px-6 py-10"
        >
          <div className="liquid-bg" aria-hidden="true" />
          <h1 className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-purple-600 bg-clip-text text-transparent">
            Gallery & Highlights
          </h1>
          <p className="mt-3 text-white/80 max-w-2xl mx-auto">
            A curated look at our achievements, launches, events, and community moments.
          </p>
        </motion.div>

        {/* Filter Chips */}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {tags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTag(t)}
              className={`px-3 py-2 rounded-full border transition focus:outline-none ${
                activeTag === t
                  ? 'bg-cyan-500/30 border-cyan-400 text-cyan-100'
                  : 'bg-white/5 border-white/10 text-white hover:bg-white/10'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Masonry Grid */}
        <div className="mt-10 [column-fill:balance] columns-1 sm:columns-2 lg:columns-3 gap-6">
          {filtered.map((item, idx) => (
            <GalleryCard key={item.id} item={item} index={idx} onOpen={() => openAt(idx)} />
          ))}
        </div>
      </section>

      {lightboxIndex !== null && (
        <Lightbox
          items={filtered}
          index={lightboxIndex}
          onClose={close}
          onPrev={prev}
          onNext={next}
        />
      )}
    </main>
  );
}
